"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { UnitInterest } from "@/interfaces/propertyinterface";
import { UnlockModal } from "../UnlockModal";
import { ProjectEnquiryDialog } from "../project/ProjectEnquiryDialog";
import { stackmapSrc, track3d, type StackmapEmbed } from "@/lib/v2/stackmap";

type Gate = "price" | "plan";

// Messages come from our own page in the frame, but only known fields are kept.
function readHome(value: unknown): UnitInterest | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const text = (key: string) => (typeof raw[key] === "string" ? (raw[key] as string).slice(0, 80) : undefined);
  const area = typeof raw.area === "number" && Number.isFinite(raw.area) ? raw.area : undefined;
  return { unit: text("unit"), plan: text("plan"), type: text("type"), area };
}

const GATE_COPY: Record<Gate, (name: string) => { heading: string; body: string; submitLabel: string }> = {
  price: (name) => ({
    heading: `See every price for ${name}`,
    body: "Enter your details once. Every full price and floor plan opens, in 3D and on the price list.",
    submitLabel: "Show me the prices",
  }),
  plan: (name) => ({
    heading: `See the floor plans for ${name}`,
    body: "Enter your details once. Every floor plan and full price opens, in 3D and on the price list.",
    submitLabel: "Show me the floor plans",
  }),
};

/**
 * The 3D model in a frame, plus the site's own sign-up and enquiry forms.
 * The frame asks for them by message; one sign-up per project unlocks the
 * 3D and the price list together (lib/v2/unlock, 30 days).
 */
export function StackMapFrame({
  slug,
  propertyId,
  propertyName,
  embed,
  place,
  hash,
  className,
  onExpand,
  onClose,
  onHash,
}: {
  slug: string;
  propertyId: string;
  propertyName: string;
  embed: StackmapEmbed;
  place: string;
  hash?: string;
  className?: string;
  onExpand?: () => void;
  onClose?: () => void;
  onHash?: (hash: string) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  // The frame URL is fixed at mount; later mode changes go by message so the model never reloads.
  const [src] = useState(() => stackmapSrc(slug, propertyId, embed, hash));
  const [gate, setGate] = useState<Gate | null>(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [home, setHome] = useState<UnitInterest | null>(null);
  const ready = useRef(false);
  const tapped = useRef(false);
  const embedRef = useRef(embed);
  const handlers = useRef({ onExpand, onClose, onHash });
  handlers.current = { onExpand, onClose, onHash };

  const post = useCallback((message: Record<string, unknown>) => {
    frameRef.current?.contentWindow?.postMessage({ source: "aip-site", ...message }, window.location.origin);
  }, []);

  useEffect(() => {
    embedRef.current = embed;
    if (ready.current) post({ type: "mode", mode: embed });
  }, [embed, post]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frameRef.current?.contentWindow) return;
      const message = event.data as { source?: string; type?: string; what?: string; home?: unknown; hash?: unknown };
      if (!message || message.source !== "aip3d") return;
      switch (message.type) {
        case "ready":
          ready.current = true;
          post({ type: "mode", mode: embedRef.current });
          track3d("3d_open", { project: slug, place });
          break;
        case "home":
          if (ready.current && !tapped.current) {
            tapped.current = true;
            track3d("3d_home_tap", { project: slug, place });
          }
          break;
        case "unlock": {
          const what: Gate = message.what === "plan" ? "plan" : "price";
          setHome(readHome(message.home));
          // Keys go to the form, not the 3D, so Escape closes it.
          frameRef.current?.blur();
          setGate(what);
          track3d("3d_gate_shown", { project: slug, place, what });
          break;
        }
        case "enquire":
          setHome(readHome(message.home));
          frameRef.current?.blur();
          setEnquiryOpen(true);
          break;
        case "expand":
          handlers.current.onExpand?.();
          break;
        case "close":
          handlers.current.onClose?.();
          break;
        case "hash":
          if (typeof message.hash === "string") handlers.current.onHash?.(message.hash);
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [place, post, slug]);

  const copy = gate ? GATE_COPY[gate](propertyName) : null;

  return (
    <>
      <iframe
        ref={frameRef}
        src={src}
        title={`${propertyName} in 3D`}
        className={className}
        allow="fullscreen"
      />
      <UnlockModal
        open={Boolean(gate)}
        onClose={() => setGate(null)}
        propertyId={propertyId}
        propertyName={propertyName}
        heading={copy?.heading}
        body={copy?.body}
        submitLabel={copy?.submitLabel}
        formName="3D unlock"
        unitInterest={home ?? undefined}
        onUnlocked={() => {
          track3d("3d_gate_sent", { project: slug, place, what: gate ?? "price" });
          post({ type: "unlocked" });
        }}
      />
      <ProjectEnquiryDialog
        open={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        propertyId={propertyId}
        propertyName={propertyName}
        interest={home}
      />
    </>
  );
}
